// Theme color definitions with defaults for all 3 groups

// Luminance-based color-scheme detection: parses common CSS color formats
// and returns "dark" if the color is perceptually dark, else "light".
// Used to auto-toggle `color-scheme` so native form controls (date/time
// pickers, select dropdowns) render with icons that contrast the theme.
function getLuminance(color) {
  if (!color || typeof color !== 'string') return 0;
  let r, g, b;
  const hex = color.trim().match(/^#([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/);
  const rgb = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else if (rgb) {
    r = +rgb[1]; g = +rgb[2]; b = +rgb[3];
  } else {
    return 0;
  }
  // ITU-R BT.601 perceptual luma
  return (r * 299 + g * 587 + b * 114) / 1000 / 255;
}
export function schemeFor(color) {
  return getLuminance(color) < 0.5 ? 'dark' : 'light';
}

export const WEBSITE_COLORS = [
  { key: 'primary', label: 'Primary Color', default: '#1a2332' },
  { key: 'accent', label: 'Accent Color', default: '#0D9488' },
  { key: 'heading_color', label: 'Heading Color', default: '#1a2332' },
  { key: 'body_text', label: 'Body Text Color', default: '#475569' },
  { key: 'navbar_bg', label: 'Navbar Background', default: '#ffffff' },
  { key: 'button_bg', label: 'Button Background', default: '#1a2332' },
  { key: 'button_text', label: 'Button Text', default: '#ffffff' },
  { key: 'button_hover_bg', label: 'Button Hover Background', default: '#c9a227' },
  { key: 'button_hover_text', label: 'Button Hover Text', default: '#111827' },
  { key: 'link_color', label: 'Link Color', default: '#0D9488' },
  { key: 'tab_active_bg', label: 'Tab Active Background', default: '#1a2332' },
  { key: 'tab_active_text', label: 'Tab Active Text', default: '#ffffff' },
  { key: 'icon_color', label: 'Icon Color', default: '#0D9488' },
  { key: 'page_bg', label: 'Page Background', default: '#ffffff' },
  { key: 'section_bg', label: 'Section Alt Background', default: '#F8FAFC' },
  { key: 'card_bg', label: 'Card Background', default: '#ffffff' },
  { key: 'card_border', label: 'Card Border', default: '#e2e8f0' },
  { key: 'footer_bg', label: 'Footer Background', default: '#1a2332' },
  { key: 'footer_text', label: 'Footer Text', default: '#ffffff' },
];

export const MYACCOUNT_COLORS = [
  { key: 'page_bg', label: 'Page Background', default: '#0d0f14' },
  { key: 'sidebar_bg', label: 'Sidebar Background', default: '#13161e' },
  { key: 'sidebar_text', label: 'Sidebar Text', default: '#9ca3af' },
  { key: 'sidebar_active_bg', label: 'Sidebar Active Background', default: 'rgba(201,168,76,0.1)' },
  { key: 'sidebar_active_text', label: 'Sidebar Active Text', default: '#c9a84c' },
  { key: 'sidebar_active_border', label: 'Sidebar Active Border', default: '#c9a84c' },
  { key: 'header_bg', label: 'Header Background', default: '#13161e' },
  { key: 'card_bg', label: 'Card Background', default: '#13161e' },
  { key: 'card_border', label: 'Card Border', default: 'rgba(255,255,255,0.05)' },
  { key: 'accent', label: 'Accent Color', default: '#c9a84c' },
  { key: 'text_primary', label: 'Primary Text', default: '#ffffff' },
  { key: 'text_secondary', label: 'Secondary Text', default: '#9ca3af' },
  { key: 'text_muted', label: 'Muted Text', default: '#6b7280' },
  { key: 'input_bg', label: 'Input Background', default: '#0d0f14' },
  { key: 'input_border', label: 'Input Border', default: 'rgba(255,255,255,0.1)' },
  { key: 'button_bg', label: 'Button Background', default: '#c9a84c' },
  { key: 'button_text', label: 'Button Text', default: '#0d0f14' },
  { key: 'tab_active', label: 'Tab Active Color', default: '#c9a84c' },
  { key: 'tab_inactive', label: 'Tab Inactive Color', default: '#6b7280' },
  { key: 'modal_bg', label: 'Modal Background', default: '#13161e' },
  { key: 'modal_border', label: 'Modal Border', default: 'rgba(255,255,255,0.1)' },
  { key: 'progress_low', label: 'Progress Bar Low', default: '#ef4444' },
  { key: 'progress_mid', label: 'Progress Bar Mid', default: '#c9a84c' },
  { key: 'progress_high', label: 'Progress Bar High', default: '#22c55e' },
  { key: 'avatar_border', label: 'Avatar Border', default: 'rgba(201,168,76,0.3)' },
  { key: 'avatar_bg', label: 'Avatar Background', default: 'rgba(201,168,76,0.1)' },
  { key: 'bell_icon', label: 'Notification Bell Icon', default: '#c9a84c' },
  { key: 'bell_hover_bg', label: 'Notification Bell Hover BG', default: 'rgba(201,168,76,0.12)' },
  { key: 'bell_badge_bg', label: 'Notification Badge Background', default: '#ef4444' },
  { key: 'bell_badge_text', label: 'Notification Badge Text', default: '#ffffff' },
];

/**
 * MY ACCOUNT 2.0 — its own palette group (`theme_colors.my_account_2`).
 *
 * Every value below was hardcoded in `pages/myaccount2/velzon-theme.css` until
 * 2026-07-30; the defaults here ARE those values, so switching the group on
 * changes nothing visually until an operator edits a field.
 *
 * All keys inject as `--ma2-<key>`. The 2.0 stylesheet then maps the portal-safe
 * `--ma-*` names off them inside `.ma2-scope`, which is why one group can drive
 * both the component kit and the scope overrides.
 *
 * ⚠ Do NOT fold this into `my_account`: that group paints the LIVE 1.0 portal
 * for every brand. Keeping them apart is what lets the 2.0 be repainted (and,
 * later, get its own light/dark switch) without touching production.
 */
export const MYACCOUNT2_COLORS = [
  // Brand + status (the kit's semantic set)
  { key: 'primary', label: 'Primary (brand)', default: '#405189' },
  { key: 'primary_hover', label: 'Primary — Hover', default: '#364574' },
  { key: 'success', label: 'Success / Accent', default: '#0ab39c' },
  { key: 'info', label: 'Info', default: '#299cdb' },
  { key: 'warning', label: 'Warning', default: '#f7b84b' },
  { key: 'danger', label: 'Danger', default: '#f06548' },
  // Surfaces
  { key: 'page_bg', label: 'Page Background', default: '#f3f3f9' },
  { key: 'card_bg', label: 'Card Background', default: '#ffffff' },
  { key: 'header_bg', label: 'Header Background', default: '#ffffff' },
  { key: 'modal_bg', label: 'Modal Background', default: '#ffffff' },
  { key: 'light', label: 'Light Surface (table head, wells)', default: '#f3f6f9' },
  { key: 'track', label: 'Track (progress, sliders)', default: '#eff2f7' },
  // Lines
  { key: 'border', label: 'Hairline / Divider', default: '#e9ebec' },
  { key: 'card_border', label: 'Card Border', default: '#e9ebec' },
  { key: 'modal_border', label: 'Modal Border', default: '#e9ebec' },
  { key: 'input_border', label: 'Input Border', default: '#ced4da' },
  // Text
  { key: 'body', label: 'Body Text', default: '#212529' },
  { key: 'heading', label: 'Heading Text', default: '#495057' },
  { key: 'muted', label: 'Muted Text', default: '#878a99' },
  { key: 'faint', label: 'Faint Text', default: '#adb5bd' },
  // Sidebar
  { key: 'sidebar_bg', label: 'Sidebar Background', default: '#405189' },
  { key: 'sidebar_text', label: 'Sidebar Text', default: '#abb9e8' },
  { key: 'sidebar_active_bg', label: 'Sidebar Active Background', default: 'rgba(255,255,255,0.10)' },
  { key: 'sidebar_active_text', label: 'Sidebar Active Text', default: '#ffffff' },
  { key: 'sidebar_active_border', label: 'Sidebar Active Border', default: '#0ab39c' },
  { key: 'sidebar_hover_bg', label: 'Sidebar Hover Background', default: 'rgba(255,255,255,0.07)' },
  // Controls
  { key: 'input_bg', label: 'Input Background', default: '#ffffff' },
  { key: 'button_bg', label: 'Button Background', default: '#0ab39c' },
  { key: 'button_text', label: 'Button Text', default: '#ffffff' },
  { key: 'tab_active', label: 'Tab Active', default: '#0ab39c' },
  { key: 'tab_inactive', label: 'Tab Inactive', default: '#878a99' },
  // Progress
  { key: 'progress_low', label: 'Progress — Low', default: '#f06548' },
  { key: 'progress_mid', label: 'Progress — Mid', default: '#f7b84b' },
  { key: 'progress_high', label: 'Progress — High', default: '#0ab39c' },
  // Avatar + notifications
  { key: 'avatar_border', label: 'Avatar Border', default: 'rgba(10,179,156,0.35)' },
  { key: 'avatar_bg', label: 'Avatar Background', default: 'rgba(10,179,156,0.10)' },
  { key: 'bell_icon', label: 'Notification Bell Icon', default: '#878a99' },
  { key: 'bell_hover_bg', label: 'Notification Bell Hover BG', default: '#f3f6f9' },
  { key: 'bell_badge_bg', label: 'Notification Badge Background', default: '#f06548' },
  { key: 'bell_badge_text', label: 'Notification Badge Text', default: '#ffffff' },
  // Charts — a FIXED, colour-vision-validated order. ⚠ Re-validate before editing:
  // adjacent pairs must stay ≥8 ΔE under deuteranopia (see D-2026-82).
  { key: 'chart_1', label: 'Chart Series 1', default: '#3577f1' },
  { key: 'chart_2', label: 'Chart Series 2', default: '#0ab39c' },
  { key: 'chart_3', label: 'Chart Series 3', default: '#6559cc' },
  { key: 'chart_4', label: 'Chart Series 4', default: '#eb9e34' },
  { key: 'chart_5', label: 'Chart Series 5', default: '#45cb85' },
  { key: 'chart_6', label: 'Chart Series 6', default: '#f06548' },
  { key: 'chart_7', label: 'Chart Series 7', default: '#299cdb' },
  { key: 'chart_other', label: 'Chart — "Other" bucket', default: '#878a99' },
  // Interaction + state tokens (added with light/dark, D-2026-84)
  { key: 'hover', label: 'Hover Fill', default: '#e2e7ee' },
  { key: 'focus_border', label: 'Focus Border', default: '#a3aed0' },
  { key: 'border_strong', label: 'Strong Divider (table total)', default: '#dde1e6' },
  { key: 'skel_shine', label: 'Skeleton Shine', default: '#f8fafc' },
  { key: 'warning_fg', label: 'Warning Text (on tint)', default: '#8a5e0e' },
  { key: 'danger_fg', label: 'Danger Text (on tint)', default: '#c23a1f' },
  { key: 'success_fg', label: 'Success Text (on tint)', default: '#0a8f7e' },
  // Mismo criterio que los otros `-fg`: el azul de marca (#299cdb) sobre su
  // propio lavado da 2.6:1 y suspende AA. Medido: #1c6f9e = 5.2:1.
  { key: 'info_fg', label: 'Info Text (on tint)', default: '#1c6f9e' },
];

/**
 * MY ACCOUNT 2.0 — DARK mode (`theme_colors.my_account_2_dark`).
 *
 * Same keys as the light group; the 2.0 injects ONE of the two sets depending on
 * the member's chosen mode (see `lib/ma2Theme.js`). Both are CMS-editable.
 *
 * ⚠ Values READ FROM THE REFERENCE, not guessed: taken off the live Velzon dark
 * theme (`data-bs-theme="dark"`) — body-bg #1a1d21, card #212529, border #32383e,
 * body text #ced4da, light surface #282b2e, input #202328.
 *
 * ⚠⚠ THE BRAND AND STATUS COLOURS ARE IDENTICAL TO LIGHT — primary #405189,
 * success, info, warning, danger. That is how the reference behaves and what
 * Anthony asked for: in dark only the SURFACES and the TEXT change; a button
 * keeps the colour the CMS gave it. White on #405189 measures 7.62:1, so the
 * button needs no dark-specific variant. Do not "adapt" these per mode.
 *
 * Two deliberate deviations from the reference, both measured on the dark card:
 *  · sidebar text #9aa1ac (5.93:1) instead of Velzon's #7c7f90, which is 3.89:1
 *    and fails AA for the small nav labels;
 *  · heading #e9ecef instead of #ced4da, to keep the heading/body hierarchy the
 *    light mode has (Velzon collapses both to one tone).
 * Text ramp on #212529: body 10.3:1 · muted ~7:1 · faint 4.5:1.
 *  · the chart series are a DIFFERENT set, validated on the dark surface. The
 *    light series FAIL there: the OKLCH lightness band for dark is 0.48–0.67,
 *    so four of the light hues sit above it. Re-validate with
 *    `scripts/validate_palette.js --mode dark --surface "#212529"` before editing.
 */
export const MYACCOUNT2_DARK_COLORS = [
  { key: 'primary', label: 'Primary (brand)', default: '#405189' },
  { key: 'primary_hover', label: 'Primary — Hover', default: '#4a5c9c' },
  { key: 'success', label: 'Success / Accent', default: '#0ab39c' },
  { key: 'info', label: 'Info', default: '#299cdb' },
  { key: 'warning', label: 'Warning', default: '#f7b84b' },
  { key: 'danger', label: 'Danger', default: '#f06548' },
  { key: 'page_bg', label: 'Page Background', default: '#1a1d21' },
  { key: 'card_bg', label: 'Card Background', default: '#212529' },
  { key: 'header_bg', label: 'Header Background', default: '#212529' },
  { key: 'modal_bg', label: 'Modal Background', default: '#212529' },
  { key: 'light', label: 'Light Surface (table head, wells)', default: '#282b2e' },
  { key: 'track', label: 'Track (progress, sliders)', default: '#2a2d31' },
  { key: 'border', label: 'Hairline / Divider', default: '#32383e' },
  { key: 'card_border', label: 'Card Border', default: '#32383e' },
  { key: 'modal_border', label: 'Modal Border', default: '#32383e' },
  { key: 'input_border', label: 'Input Border', default: '#32383e' },
  { key: 'body', label: 'Body Text', default: '#ced4da' },
  { key: 'heading', label: 'Heading Text', default: '#e9ecef' },
  { key: 'muted', label: 'Muted Text', default: '#adb5bd' },
  { key: 'faint', label: 'Faint Text', default: '#878a99' },
  { key: 'sidebar_bg', label: 'Sidebar Background', default: '#212529' },
  { key: 'sidebar_text', label: 'Sidebar Text', default: '#9aa1ac' },
  { key: 'sidebar_active_bg', label: 'Sidebar Active Background', default: 'rgba(255,255,255,0.07)' },
  { key: 'sidebar_active_text', label: 'Sidebar Active Text', default: '#ffffff' },
  { key: 'sidebar_active_border', label: 'Sidebar Active Border', default: '#0ab39c' },
  { key: 'sidebar_hover_bg', label: 'Sidebar Hover Background', default: 'rgba(255,255,255,0.05)' },
  { key: 'input_bg', label: 'Input Background', default: '#202328' },
  { key: 'button_bg', label: 'Button Background', default: '#0ab39c' },
  { key: 'button_text', label: 'Button Text', default: '#ffffff' },
  { key: 'tab_active', label: 'Tab Active', default: '#0ab39c' },
  { key: 'tab_inactive', label: 'Tab Inactive', default: '#9aa1ac' },
  { key: 'progress_low', label: 'Progress — Low', default: '#f06548' },
  { key: 'progress_mid', label: 'Progress — Mid', default: '#f7b84b' },
  { key: 'progress_high', label: 'Progress — High', default: '#0ab39c' },
  { key: 'avatar_border', label: 'Avatar Border', default: 'rgba(10,179,156,0.40)' },
  { key: 'avatar_bg', label: 'Avatar Background', default: 'rgba(10,179,156,0.14)' },
  { key: 'bell_icon', label: 'Notification Bell Icon', default: '#adb5bd' },
  { key: 'bell_hover_bg', label: 'Notification Bell Hover BG', default: '#2a2f34' },
  { key: 'bell_badge_bg', label: 'Notification Badge Background', default: '#f06548' },
  { key: 'bell_badge_text', label: 'Notification Badge Text', default: '#ffffff' },
  // Validated on #212529 — do not reuse the light series here.
  { key: 'chart_1', label: 'Chart Series 1', default: '#4a86f5' },
  { key: 'chart_2', label: 'Chart Series 2', default: '#0a9d89' },
  { key: 'chart_3', label: 'Chart Series 3', default: '#7a6dd6' },
  { key: 'chart_4', label: 'Chart Series 4', default: '#bd7e28' },
  { key: 'chart_5', label: 'Chart Series 5', default: '#31a066' },
  { key: 'chart_6', label: 'Chart Series 6', default: '#dd4f33' },
  { key: 'chart_7', label: 'Chart Series 7', default: '#2f92cc' },
  { key: 'chart_other', label: 'Chart — "Other" bucket', default: '#7a828c' },
  { key: 'hover', label: 'Hover Fill', default: 'rgba(255,255,255,0.07)' },
  { key: 'focus_border', label: 'Focus Border', default: '#5a6bab' },
  { key: 'border_strong', label: 'Strong Divider (table total)', default: '#3b4148' },
  { key: 'skel_shine', label: 'Skeleton Shine', default: '#2c3238' },
  { key: 'warning_fg', label: 'Warning Text (on tint)', default: '#f7c56b' },
  { key: 'danger_fg', label: 'Danger Text (on tint)', default: '#f6a08c' },
  { key: 'success_fg', label: 'Success Text (on tint)', default: '#3fd6c0' },
  { key: 'info_fg', label: 'Info Text (on tint)', default: '#7cc4ea' },
];

export const LANDING_PAGE_COLORS = [
  { key: 'bg_base', label: 'Base Background', default: '#0a0a12' },
  { key: 'overlay_start', label: 'Overlay Start (top)', default: 'rgba(0,0,0,0.75)' },
  { key: 'overlay_end', label: 'Overlay End (bottom)', default: 'rgba(5,5,15,0.88)' },
  { key: 'accent', label: 'Accent / Gold', default: '#c9a84c' },
  { key: 'heading', label: 'Heading Color', default: '#f5f5f5' },
  { key: 'body_text', label: 'Body Text', default: '#f5f5f5' },
  { key: 'secondary_text', label: 'Secondary Text', default: '#a0a0b0' },
  { key: 'border', label: 'Border / Separator', default: 'rgba(201,168,76,0.3)' },
  { key: 'button_bg', label: 'Button Background', default: '#c9a84c' },
  { key: 'button_text', label: 'Button Text', default: '#0a0a12' },
  { key: 'button_outline_border', label: 'Outline Button Border', default: '#c9a84c' },
  { key: 'button_outline_text', label: 'Outline Button Text', default: '#c9a84c' },
  { key: 'input_bg', label: 'Input Background', default: 'rgba(255,255,255,0.05)' },
  { key: 'input_border', label: 'Input Border', default: 'rgba(201,168,76,0.3)' },
  { key: 'input_text', label: 'Input Text', default: '#f5f5f5' },
  { key: 'input_placeholder', label: 'Input Placeholder', default: '#a0a0b0' },
  { key: 'modal_bg', label: 'Modal Background', default: '#13161e' },
  { key: 'modal_border', label: 'Modal Border', default: 'rgba(201,168,76,0.3)' },
  { key: 'footer_bg', label: 'Footer Background', default: 'rgba(0,0,0,0.3)' },
  { key: 'footer_text', label: 'Footer Text', default: '#a0a0b0' },
  { key: 'cookie_bg', label: 'Cookie Banner Background', default: '#13161e' },
  { key: 'cookie_text', label: 'Cookie Banner Text', default: '#a0a0b0' },
  { key: 'countdown_bg', label: 'Countdown Box Background', default: 'rgba(255,255,255,0.05)' },
  { key: 'countdown_number', label: 'Countdown Number', default: '#c9a84c' },
  { key: 'countdown_label', label: 'Countdown Label', default: '#a0a0b0' },
];

export const ADMIN_COLORS = [
  { key: 'sidebar_text', label: 'Sidebar Text', default: 'rgba(255,255,255,0.6)' },
  { key: 'sidebar_active_bg', label: 'Sidebar Active Background', default: '#0D9488' },
  { key: 'sidebar_active_text', label: 'Sidebar Active Text', default: '#ffffff' },
  { key: 'sidebar_hover_bg', label: 'Sidebar Hover Background', default: 'rgba(255,255,255,0.05)' },
  { key: 'navbar_bg', label: 'Navbar Background', default: '#ffffff' },
  { key: 'navbar_text', label: 'Navbar Text', default: '#1a2332' },
  { key: 'navbar_border', label: 'Navbar Border', default: '#e2e8f0' },
  { key: 'page_bg', label: 'Page Background', default: '#f8fafc' },
  { key: 'card_bg', label: 'Card Background', default: '#ffffff' },
  { key: 'card_border', label: 'Card Border', default: '#e2e8f0' },
  { key: 'accent', label: 'Accent Color', default: '#0D9488' },
  { key: 'button_bg', label: 'Button Background', default: '#0D9488' },
  { key: 'button_text', label: 'Button Text', default: '#ffffff' },
  { key: 'button_danger_bg', label: 'Danger Button Background', default: '#ef4444' },
  { key: 'heading', label: 'Heading Color', default: '#1a2332' },
  { key: 'text_primary', label: 'Primary Text', default: '#334155' },
  { key: 'text_secondary', label: 'Secondary Text', default: '#64748b' },
  { key: 'table_header_bg', label: 'Table Header Background', default: '#f8fafc' },
  { key: 'table_border', label: 'Table Border', default: '#e2e8f0' },
  { key: 'table_row_hover', label: 'Table Row Hover', default: '#f1f5f9' },
  { key: 'input_border', label: 'Input Border', default: '#e2e8f0' },
  { key: 'input_focus', label: 'Input Focus Border', default: '#0D9488' },
  { key: 'badge_bg', label: 'Badge Background', default: '#0D9488' },
  { key: 'badge_text', label: 'Badge Text', default: '#ffffff' },
];

export const ENROLLMENT_COLORS = [
  { key: 'page_bg', label: 'Page Background', default: '#f4f4f4' },
  { key: 'header_bg', label: 'Header Bar Background', default: '#F5A623' },
  { key: 'header_text', label: 'Header Text', default: '#ffffff' },
  { key: 'footer_bg', label: 'Footer Background', default: '#1a2535' },
  { key: 'footer_text', label: 'Footer Text', default: '#9ca3af' },
  { key: 'step_title', label: 'Step Title Color', default: '#1a2535' },
  { key: 'step_active', label: 'Step Active Circle', default: '#F5A623' },
  { key: 'step_completed', label: 'Step Completed Circle', default: '#F5A623' },
  { key: 'step_pending', label: 'Step Pending Circle', default: '#d4d4d4' },
  { key: 'progress_bar', label: 'Progress Bar Fill', default: '#F5A623' },
  { key: 'progress_bg', label: 'Progress Bar Track', default: '#e5e7eb' },
  { key: 'form_bg', label: 'Form Background', default: '#ffffff' },
  { key: 'label_color', label: 'Field Label Color', default: '#374151' },
  { key: 'input_text', label: 'Input Text', default: '#1a2535' },
  { key: 'input_border', label: 'Input Bottom Border', default: '#d1d5db' },
  { key: 'input_focus', label: 'Input Focus Border', default: '#F5A623' },
  { key: 'input_icon', label: 'Input Icon Color', default: '#9ca3af' },
  { key: 'placeholder', label: 'Placeholder Color', default: '#9ca3af' },
  { key: 'save_btn_border', label: 'Save Button Border', default: '#dc2626' },
  { key: 'save_btn_text', label: 'Save Button Text', default: '#dc2626' },
  { key: 'continue_btn_border', label: 'Continue Button Border', default: '#1a2535' },
  { key: 'continue_btn_text', label: 'Continue Button Text', default: '#1a2535' },
  { key: 'submit_btn_border', label: 'Submit Button Border', default: '#1a2535' },
  { key: 'submit_btn_text', label: 'Submit Button Text', default: '#1a2535' },
  { key: 'error_color', label: 'Error Message Color', default: '#dc2626' },
  { key: 'success_color', label: 'Success Color', default: '#16a34a' },
  { key: 'section_title', label: 'Section Title', default: '#1a2535' },
  { key: 'tooltip_bg', label: 'Tooltip Background', default: '#1a2535' },
  { key: 'tooltip_text', label: 'Tooltip Text', default: '#ffffff' },
];

// AUX Projects (PMS) palette — the standalone Projects product at
// pms.<brand>.com reads this group over its brand endpoint and injects the
// values as --pm-* CSS variables at runtime (its whole UI is built on
// var(--pm-*), so this palette re-themes the entire app). Dark register by
// design; keys mirror the --pm-* tokens in the PMS index.css.
export const PMS_COLORS = [
  { key: 'bg',         label: 'App Background',            default: '#101318' },
  { key: 'surface',    label: 'Surface / Card',            default: '#171b22' },
  { key: 'raised',     label: 'Raised Surface',            default: '#1e232c' },
  { key: 'border',     label: 'Border',                    default: '#2a303b' },
  { key: 'sidebar',    label: 'Sidebar Background',        default: '#171b22' },
  { key: 'topbar',     label: 'Top Bar Background',        default: '#101318' },
  { key: 'input',      label: 'Input Background',          default: '#101318' },
  { key: 'text',       label: 'Primary Text',              default: '#e8eaee' },
  { key: 'muted',      label: 'Muted Text',                default: '#9aa3b2' },
  { key: 'faint',      label: 'Faint Text',                default: '#6b7383' },
  { key: 'accent',     label: 'Accent',                    default: '#c8a557' },
  { key: 'accent_ink', label: 'Accent Ink (text on accent)', default: '#14110a' },
  { key: 'danger',     label: 'Danger',                    default: '#e2635c' },
  { key: 'success',    label: 'Success',                   default: '#4ea87a' },
  { key: 'warning',    label: 'Warning',                   default: '#d9a441' },
  { key: 'info',       label: 'Info',                      default: '#5b8dd9' },
];

// KMS (insights.<brand>.com) palette — the 11 semantic tokens of the KMS public
// theme. NOT stored in db.settings: the API reads/writes them straight from the
// KMS's own settings (kms_settings.general.theme_colors, same fields as the KMS
// admin → Colors), and serves the active KMS theme's defaults as
// `settings.kms_theme_defaults` (these Moderna values are only the fallback).
// Band/Accent/Box/Line/Body/Secondary also tint the iiReport ranking documents.
export const KMS_COLORS = [
  { key: 'band',     label: 'Band / Masthead',                  default: '#1B2A4A' },
  // Report-only: iiReport title bars + table headers inside posts; leaves the
  // masthead alone. Its placeholder is the colour reports use today (from the API).
  { key: 'report_band', label: 'Report Headers (internal pages)', default: '#1B2A4A' },
  { key: 'heading',  label: 'Headings / Titles',                default: '#1B2A4A' },
  { key: 'accent',   label: 'Accent (rules / tabs / bullets)',  default: '#C9A24B' },
  { key: 'kicker',   label: 'Kicker Text',                      default: '#C9A24B' },
  { key: 'box_bg',   label: 'Box / Callout Background',         default: '#F6F5F1' },
  { key: 'ink',      label: 'Body Text',                        default: '#1f2530' },
  { key: 'muted',    label: 'Secondary Text',                   default: '#6a6456' },
  { key: 'line',     label: 'Hairlines / Borders',              default: '#ddd9cf' },
  { key: 'row_alt',  label: 'Alternate Table Rows',             default: '#f6f5f1' },
  { key: 'negative', label: 'Negative / Down',                  default: '#9e3b2f' },
  { key: 'positive', label: 'Positive / Up',                    default: '#1f6b48' },
];

// Helper: get color value from settings with fallback to default
export function getColor(group, key, themeColors) {
  return themeColors?.[group]?.[key] || getDefault(group, key);
}

function getDefault(group, key) {
  const groups = { website: WEBSITE_COLORS, my_account: MYACCOUNT_COLORS, my_account_2: MYACCOUNT2_COLORS, my_account_2_dark: MYACCOUNT2_DARK_COLORS, admin: ADMIN_COLORS, landing_page: LANDING_PAGE_COLORS, enrollment: ENROLLMENT_COLORS, pms: PMS_COLORS, kms: KMS_COLORS };
  return groups[group]?.find(c => c.key === key)?.default || '#000000';
}

// Inject all CSS variables from theme_colors settings.
// `overrides` can pin a specific color-scheme explicitly, e.g.:
//   { my_account_color_scheme: 'dark' | 'light' }
// When an override is provided it takes precedence over the luminance-derived scheme.
export function injectThemeColors(themeColors, overrides = {}) {
  const root = document.documentElement;

  // Website colors (--color-* for backwards compat)
  const ws = themeColors?.website || {};
  WEBSITE_COLORS.forEach(c => {
    const val = ws[c.key] || c.default;
    root.style.setProperty(`--color-${c.key.replace(/_/g, '-')}`, val);
    // Legacy aliases
    if (c.key === 'heading_color') root.style.setProperty('--color-heading', val);
    if (c.key === 'body_text') root.style.setProperty('--color-body-text', val);
    if (c.key === 'link_color') root.style.setProperty('--color-link', val);
    if (c.key === 'footer_bg') root.style.setProperty('--color-footer-bg', val);
    if (c.key === 'footer_text') root.style.setProperty('--color-footer-text', val);
    if (c.key === 'button_bg') root.style.setProperty('--color-button-bg', val);
    if (c.key === 'button_text') root.style.setProperty('--color-button-text', val);
    if (c.key === 'button_hover_bg') root.style.setProperty('--color-button-hover-bg', val);
    if (c.key === 'button_hover_text') root.style.setProperty('--color-button-hover-text', val);
    // Page Background: alias to the PB/Aurex `--color-bg` the one-page templates read.
    if (c.key === 'page_bg') root.style.setProperty('--color-bg', val);
    if (c.key === 'tab_active_bg') root.style.setProperty('--color-tab-active-bg', val);
    if (c.key === 'tab_active_text') root.style.setProperty('--color-tab-active-text', val);
    if (c.key === 'icon_color') root.style.setProperty('--color-icon', val);
  });
  // Text/icons drawn ON the accent (Private Community Pro buttons, chips, the
  // hero ball). Auto-contrast so a dark brand accent gets white ink instead of
  // the dark ink that only worked for Aurex's light green. Shades of the accent
  // (--accent-soft/strong/deep/tint) are derived in index.css with color-mix.
  const accent = ws.accent || (WEBSITE_COLORS.find(c => c.key === 'accent') || {}).default;
  root.style.setProperty('--accent-ink', aurexContrastFor(accent) === 'light' ? '#ffffff' : '#0f172a');

  // My Account colors (--ma-*)
  const ma = themeColors?.my_account || {};
  MYACCOUNT_COLORS.forEach(c => {
    root.style.setProperty(`--ma-${c.key.replace(/_/g, '-')}`, ma[c.key] || c.default);
  });
  // Auto-derive color-scheme from the card bg so native pickers/selects match theme,
  // unless an explicit admin override is set in CMS Settings.
  const maCardBg = ma.card_bg || MYACCOUNT_COLORS.find(c => c.key === 'card_bg').default;
  const maScheme = (overrides.my_account_color_scheme === 'dark' || overrides.my_account_color_scheme === 'light')
    ? overrides.my_account_color_scheme
    : schemeFor(maCardBg);
  root.style.setProperty('--ma-color-scheme', maScheme);

  // My Account 2.0 colors (--ma2-*)
  // Injected on :root as INLINE styles, which beat the `:root` block in
  // velzon-theme.css — that block stays as the offline fallback. The 2.0 scope
  // maps its `--ma-*` names off these, so one group drives the whole workspace,
  // modals included (they portal out of `.ma2-scope` but still see `:root`).
  // ⚠ The MODE decides which group is written — the switch is here, in JS, not
  // in CSS. Injection writes INLINE custom properties on :root, and no stylesheet
  // rule can outrank an inline declaration, so a `:root[data-theme=dark]` block
  // would never win. One set of `--ma2-*` vars; JS picks the source.
  const ma2Dark = overrides.ma2_mode === 'dark';
  const ma2Defs = ma2Dark ? MYACCOUNT2_DARK_COLORS : MYACCOUNT2_COLORS;
  const ma2 = (ma2Dark ? themeColors?.my_account_2_dark : themeColors?.my_account_2) || {};
  ma2Defs.forEach(c => {
    root.style.setProperty(`--ma2-${c.key.replace(/_/g, '-')}`, ma2[c.key] || c.default);
  });
  // Native control chrome for the 2.0. Derived from the card bg so date pickers
  // and steppers follow the mode instead of rendering white-on-white (D-2026-82).
  const ma2CardBg = ma2.card_bg || ma2Defs.find(c => c.key === 'card_bg').default;
  root.style.setProperty('--ma2-color-scheme', schemeFor(ma2CardBg));

  // Admin colors (--ad-*)
  const ad = themeColors?.admin || {};
  ADMIN_COLORS.forEach(c => {
    root.style.setProperty(`--ad-${c.key.replace(/_/g, '-')}`, ad[c.key] || c.default);
  });
  const adCardBg = ad.card_bg || ADMIN_COLORS.find(c => c.key === 'card_bg').default;
  root.style.setProperty('--ad-color-scheme', schemeFor(adCardBg));

  // Landing Page colors (--lp-*)
  const lp = themeColors?.landing_page || {};
  LANDING_PAGE_COLORS.forEach(c => {
    root.style.setProperty(`--lp-${c.key.replace(/_/g, '-')}`, lp[c.key] || c.default);
  });

  // Membership Enrollment colors (--me-*)
  const me = themeColors?.enrollment || {};
  ENROLLMENT_COLORS.forEach(c => {
    root.style.setProperty(`--me-${c.key.replace(/_/g, '-')}`, me[c.key] || c.default);
  });
  const meFormBg = me.form_bg || ENROLLMENT_COLORS.find(c => c.key === 'form_bg').default;
  root.style.setProperty('--me-color-scheme', schemeFor(meFormBg));

  // AUX Projects / PMS colors (--pm-*). The standalone Projects app injects the
  // same tokens over its own endpoint; injecting them here too keeps the mapping
  // authoritative in one place (harmless in the CMS, which uses none of them).
  const pm = themeColors?.pms || {};
  PMS_COLORS.forEach(c => {
    root.style.setProperty(`--pm-${c.key.replace(/_/g, '-')}`, pm[c.key] || c.default);
  });

  // NOTE: the legacy `theme_colors.personal_brand` override group was removed
  // (2026-06-25). It used to be injected here LAST so it won over the Website
  // group, but it had no editor in Settings -> Colors, so operators could not
  // change those colors and their Website-tab edits were silently overridden.
  // The Website group is now the single source of truth for the public site
  // across every template (default/modern/classic/aurex/personalbrand); the PB
  // one-page sections keep their own per-section black/white auto-contrast.
}

// Theme definitions
export const THEMES = [
  {
    id: 'default',
    name: 'Default',
    description: 'Clean, professional layout with a modern business aesthetic. White background with structured sections.',
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Minimalist design with bold typography, generous spacing, and a transparent header that blends into the hero.',
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional corporate look with a boxed layout, bordered sections, and serif-heavy typography for a distinguished feel.',
  },
  {
    id: 'aurex',
    name: 'One-page Pro',
    description: 'Monochromatic one-page template — clean whites/grays/darks, per-section background & font picker, and global drag-drop ordering.',
  },
  {
    id: 'personalbrand',
    name: 'Personal Brand Pro',
    description: 'Warm, personal one-page template for independent professionals — credit analysts, AI innovators, real-estate experts. Cream/sand/terracotta defaults, serif-led typography, same section library as One-page Pro.',
  },
  {
    id: 'privatecommunity',
    name: 'Private Community Pro',
    description: 'Members-first one-page template for private communities. Starts as a One-page Pro clone (same section library, order, and content) and is redesigned section by section. Palette comes from Settings → Colors → Website.',
  },
];

// Themes that share the Aurex section architecture (per-section bg + font,
// drag-drop ordering, the AurexFooMono components).  Helper centralises the
// check so we don't sprinkle `theme === 'aurex' || theme === 'personalbrand'`
// across the codebase.
export function isAurexFamily(themeId) {
  return themeId === 'aurex' || themeId === 'personalbrand' || themeId === 'privatecommunity';
}

// Personal Brand Pro: warm defaults that the operator can override from
// Settings → Colors → Website.  These are CSS variable values applied to
// :root when the theme is active so the existing Aurex components inherit
// them without code changes.
export const PERSONALBRAND_DEFAULTS = {
  '--color-primary': '#3a2517',          // Deep espresso
  '--color-accent': '#c08552',           // Warm terracotta
  '--color-bg': '#fbf6ee',               // Cream
  '--color-card': '#ffffff',
  '--color-heading': '#2a1810',          // Near-black brown
  '--color-text': '#3d2818',
  '--color-secondary-text': '#7c6651',   // Soft warm gray
  '--color-border': '#e8dccd',           // Sand
};

// CMS-editable color palette for Personal Brand Pro (Settings → Colors).
// Keys map to themeColors.personal_brand.{key} in the DB; values are injected
// as --color-{key} CSS variables, overriding the PERSONALBRAND_DEFAULTS above.
export const PERSONALBRAND_COLORS = [
  { key: 'primary',        label: 'Primary Color',      default: '#3a2517' },
  { key: 'accent',         label: 'Accent Color',       default: '#c08552' },
  { key: 'bg',             label: 'Page Background',    default: '#fbf6ee' },
  { key: 'card',           label: 'Card Background',    default: '#ffffff' },
  { key: 'heading',        label: 'Heading Color',      default: '#2a1810' },
  { key: 'text',           label: 'Body Text',          default: '#3d2818' },
  { key: 'secondary_text', label: 'Secondary Text',     default: '#7c6651' },
  { key: 'border',         label: 'Border Color',       default: '#e8dccd' },
  { key: 'button_bg',      label: 'Button Background',  default: '#3a2517' },
  { key: 'button_text',    label: 'Button Text',        default: '#ffffff' },
];

// ── Aurex theme: official monochromatic palette ──────────────────────────
// Only these 7 swatches are allowed as section backgrounds. Text color
// flips automatically to the opposite end of the contrast curve.
export const AUREX_PALETTE = [
  { key: 'white',    label: 'Pure White', hex: '#FFFFFF' },
  { key: 'gray_50',  label: 'Gray 50',    hex: '#F9FAFB' },
  { key: 'gray_100', label: 'Gray 100',   hex: '#F4F6F8' },
  { key: 'gray_200', label: 'Gray 200',   hex: '#E5E7EB' },
  { key: 'gray_700', label: 'Gray 700',   hex: '#374151' },
  { key: 'gray_800', label: 'Gray 800',   hex: '#1F2937' },
  { key: 'gray_900', label: 'Gray 900',   hex: '#111827' },
];

// Font picker — Google Fonts loaded in public/index.html when Aurex theme active.
export const AUREX_FONTS = [
  { key: 'plus_jakarta',   label: 'Plus Jakarta Sans', css: "'Plus Jakarta Sans', 'Inter', sans-serif", note: 'Personal Brand Pro default' },
  { key: 'sora',           label: 'Sora',           css: "'Sora', sans-serif",           note: 'Modern, geometric' },
  { key: 'inter',          label: 'Inter',          css: "'Inter', sans-serif",          note: 'Neutral, legible' },
  { key: 'playfair',       label: 'Playfair Display', css: "'Playfair Display', serif",  note: 'Elegant serif' },
  { key: 'space_grotesk',  label: 'Space Grotesk',  css: "'Space Grotesk', sans-serif",  note: 'Technical' },
  { key: 'dm_sans',        label: 'DM Sans',        css: "'DM Sans', sans-serif",        note: 'Friendly' },
];

// Default font CSS string for Personal Brand Pro template
export const PB_FONT_CSS = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Returns 'dark' or 'light' based on bg luminance — used for auto-contrast.
export function aurexContrastFor(hex) {
  return schemeFor(hex) === 'dark' ? 'light' : 'dark';
}

// Full section catalog of the «Moderno» theme (legacy sections + the Aurex 2.0
// sections). Mirrors MODERN_DEFAULT_ORDER in backend/routes/admin_tools.py and
// the Modern scope of the Page Builder (SectionOrderManager).
export const MODERN_SECTION_CATALOG = ['hero', 'about', 'aurex_video', 'services', 'aurex_audience', 'testimonials', 'aurex_process', 'aurex_pricing', 'aurex_team', 'aurex_events', 'news', 'blog', 'model_portfolio', 'reading_list', 'aurex_partners', 'aurex_clients', 'portfolio', 'gallery', 'map_global', 'map_conferences', 'map_recommended', 'contact'];
